import React, { useState, useEffect } from "react";
import { Sparkles, Calculator, ScanLine, MessageSquare, UserRound, CheckCircle, ArrowRight, X, Star, Shield, ShoppingBag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/api";

const STEPS = [
  {
    title: "Welcome to AdptMeValues!",
    icon: Sparkles,
    color: "from-[#ff6fa3] to-[#c279ff]",
    description: "Your all-in-one platform for safe, smart Adopt Me trading. AI-powered scam detection, real-time value calculator, and a friendly marketplace — all in one place.",
    highlights: [
      { icon: Shield, text: "Scam-shielded chat blocks phishing & bad links automatically" },
      { icon: Calculator, text: "Value calculator with pet variants, potions, Neon & Mega" },
    ]
  },
  {
    title: "Value Calculator",
    icon: Calculator,
    color: "from-[#ff6fa3] to-[#ffb13b]",
    description: "Compare two sides of any trade side-by-side with 9 slots each. Add items, toggle Fly/Ride potions and Neon/Mega variants — get instant WIN/LOSS/FAIR verdict.",
    highlights: [
      { icon: ArrowRight, text: "Tap any slot to search & pick items from the catalog" },
      { icon: Star, text: "Tap a filled slot to change potions or variant" },
      { icon: Shield, text: "Know exactly what a trade is worth before you accept" },
    ]
  },
  {
    title: "AI Trade Scanner",
    icon: ScanLine,
    color: "from-[#7649e6] to-[#3aa8ff]",
    description: "Upload a screenshot of any in-game trade window. Our AI (powered by Gemini) identifies every item on both sides, detects Neon/Mega variants, potion icons, and flags scam attempts.",
    highlights: [
      { icon: ScanLine, text: "Drag & drop, paste (Ctrl+V), or choose a file" },
      { icon: Shield, text: "Gets scam signals like suspicious overpays or fake items" },
      { icon: CheckCircle, text: "Confidence indicators show how sure the AI is" },
    ]
  },
  {
    title: "Trade Center",
    icon: MessageSquare,
    color: "from-[#ffb13b] to-[#ff5a8a]",
    description: "Browse trades posted by the community, or create your own. Each trade has a private scam-shielded chat where bad links and harassment are auto-blocked.",
    highlights: [
      { icon: ShoppingBag, text: "Post up to 5 trades per hour, each auto-expires after 2 days" },
      { icon: Shield, text: "Every message is moderated — scammers get auto-muted" },
      { icon: CheckCircle, text: "Complete trades and leave reviews to build reputation" },
    ]
  },
  {
    title: "Your Profile & Reputation",
    icon: UserRound,
    color: "from-[#36a86b] to-[#3aa8ff]",
    description: "Build your trading reputation with reviews and ranks. From New Trader to Legendary Trader — every completed trade earns you credibility.",
    highlights: [
      { icon: Star, text: "Rate trading partners 1-5 stars with quick tags" },
      { icon: Shield, text: "Trading ranks unlock trust — Legendary Traders are proven" },
      { icon: ArrowRight, text: "Show off your inventory & wishlist on your profile" },
    ]
  },
  {
    title: "You're All Set!",
    icon: CheckCircle,
    color: "from-[#36a86b] to-[#2d8f5e]",
    description: "You now know everything to get started. Remember: if a deal seems too good to be true, it probably is. Use the calculator, scan suspicious trades, and always check profiles before trading.",
    highlights: [
      { icon: MessageSquare, text: "Need help? Chat with Trader Pup on the Support page" },
      { icon: Shield, text: "See something suspicious? Hit Report on any trade or profile" },
    ]
  },
];

export default function TutorialOverlay({ onComplete }) {
  const [step, setStep] = useState(0);
  const [closing, setClosing] = useState(false);
  const current = STEPS[step];
  const Icon = current.icon;
  const totalSteps = STEPS.length;

  const handleNext = () => {
    if (step < totalSteps - 1) {
      setStep(s => s + 1);
    }
  };

  const handlePrev = () => {
    if (step > 0) {
      setStep(s => s - 1);
    }
  };

  const handleComplete = async () => {
    setClosing(true);
    setTimeout(() => {
      localStorage.setItem("adopt_tutorial_seen", "true");
      try { api.post("/user/flags", { tutorial_seen: true }).catch(() => {}); } catch (e) {}
      onComplete();
    }, 300);
  };

  const handleSkip = async () => {
    localStorage.setItem("adopt_tutorial_seen", "true");
    try { api.post("/user/flags", { tutorial_seen: true }).catch(() => {}); } catch (e) {}
    onComplete();
  };

  const progress = ((step + 1) / totalSteps) * 100;

  return (
    <div className={`fixed inset-0 z-50 flex items-center justify-center transition-opacity duration-300 ${closing ? "opacity-0" : "opacity-100"}`}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={handleSkip}/>
      <div className={`relative w-[95vw] max-w-2xl max-h-[90vh] overflow-y-auto rounded-3xl bg-white border-4 border-[#2a1f3d]/10 shadow-cartoon-lg pop-in ${closing ? "scale-95" : ""}`} style={{ transition: "transform 0.3s ease" }}>
        {/* Progress bar */}
        <div className="absolute top-0 left-0 right-0 h-2 bg-gray-100 rounded-t-3xl overflow-hidden">
          <div className="h-full bg-gradient-to-r from-[#ff6fa3] to-[#c279ff] transition-all duration-500 ease-out rounded-full" style={{ width: `${progress}%` }}/>
        </div>

        {/* Close button */}
        <button onClick={handleSkip} className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 transition-colors z-10" aria-label="Skip tutorial">
          <X className="w-5 h-5 text-gray-400 hover:text-gray-600"/>
        </button>

        <div className="p-6 sm:p-8 md:p-10 pt-8">
          {/* Step counter */}
          <div className="text-xs font-semibold text-[#7649e6]/60 uppercase tracking-wider mb-1">
            Step {step + 1} of {totalSteps}
          </div>

          {/* Icon */}
          <div className={`w-16 h-16 sm:w-20 sm:h-20 rounded-3xl bg-gradient-to-br ${current.color} flex items-center justify-center shadow-cartoon mb-5 mx-auto`}>
            <Icon className="w-8 h-8 sm:w-10 sm:h-10 text-white"/>
          </div>

          {/* Title */}
          <h2 className="font-display text-2xl sm:text-3xl md:text-4xl text-center text-[#2a1f3d] mb-3">
            {current.title}
          </h2>

          {/* Description */}
          <p className="text-sm sm:text-base text-center text-[#2a1f3d]/70 max-w-lg mx-auto mb-6">
            {current.description}
          </p>

          {/* Highlights */}
          <div className="space-y-3 max-w-md mx-auto">
            {current.highlights.map((h, i) => (
              <div key={i} className="flex items-start gap-3 p-3 rounded-2xl bg-gradient-to-br from-[#fff7e8] to-white border-2 border-[#2a1f3d]/10">
                <div className="w-8 h-8 rounded-xl bg-[#ffd1e8]/60 flex items-center justify-center shrink-0 mt-0.5">
                  <h.icon className="w-4 h-4 text-[#ff5a8a]"/>
                </div>
                <p className="text-sm text-[#2a1f3d]/80">{h.text}</p>
              </div>
            ))}
          </div>

          {/* Navigation */}
          <div className="mt-8 flex items-center justify-between gap-3">
            <div className="flex gap-2">
              {step > 0 && (
                <Button onClick={handlePrev} variant="outline" className="rounded-2xl btn-pop">
                  Back
                </Button>
              )}
            </div>

            <div className="flex gap-2 items-center">
              {step < totalSteps - 1 && (
                <Button onClick={handleSkip} variant="ghost" className="rounded-2xl text-sm text-gray-400 hover:text-gray-600">
                  Skip
                </Button>
              )}
              {step < totalSteps - 1 ? (
                <Button onClick={handleNext} className="rounded-2xl bg-[#ff6fa3] hover:bg-[#ff5a8a] shadow-cartoon-sm btn-pop">
                  Next <ArrowRight className="w-4 h-4 ml-1"/>
                </Button>
              ) : (
                <Button onClick={handleComplete} className="rounded-2xl bg-[#36a86b] hover:bg-[#2d8f5e] shadow-cartoon-sm btn-pop text-white">
                  <CheckCircle className="w-4 h-4 mr-1"/> Get Started!
                </Button>
              )}
            </div>
          </div>

          {/* Dots */}
          <div className="flex justify-center gap-2 mt-6">
            {STEPS.map((_, i) => (
              <button key={i} onClick={() => setStep(i)}
                className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${i === step ? "bg-[#ff6fa3] w-6" : "bg-gray-300 hover:bg-gray-400"}`}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
